import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import NotificationBell from "./NotificationBell";
import GlobalCommandPalette from "./GlobalCommandPalette";
import MobileDrawerNav from "./MobileDrawerNav";
import MobileBottomNav from "./mobile/MobileBottomNav";
import BoostJoinBanner from "./BoostJoinBanner";
import { Menu } from "lucide-react";
import { DS } from "@/lib/ds";

export default function AppLayout({ title, subtitle, actions, children, noPad = false, pageBg }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar:collapsed") === "1"; } catch { return false; }
  });
  const [navOpen, setNavOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => {
    try { return window.matchMedia("(min-width: 1024px)").matches; } catch { return true; }
  });

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    const onChange = (e) => setCollapsed(!!e.detail);
    window.addEventListener("sidebar-collapsed", onChange);
    return () => window.removeEventListener("sidebar-collapsed", onChange);
  }, []);

  const ml = collapsed ? "lg:ml-[60px]" : "lg:ml-[220px]";

  return (
    <div className="min-h-screen" style={{ background: pageBg || DS.bg }}>
      {/* Sidebar solo desktop */}
      <div className="hidden lg:block"><Sidebar /></div>
      {/* Drawer nav per mobile/tablet */}
      <MobileDrawerNav open={navOpen} onOpenChange={setNavOpen} />

      <main className={`${ml} transition-[margin] duration-200 ease-out`}>

        {/* ── Sticky header ── */}
        <header
          className="sticky top-0 z-30"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.88) 100%)',
            backdropFilter: 'blur(28px) saturate(200%)',
            WebkitBackdropFilter: 'blur(28px) saturate(200%)',
            borderBottom: `1px solid rgba(10,25,47,0.07)`,
            boxShadow: '0 4px 20px -8px rgba(10,25,47,0.08), inset 0 -1px 0 rgba(183,149,91,0.10)',
          }}
          data-testid="app-header"
        >
          <div className="px-4 sm:px-8 py-3 sm:py-4 min-h-[60px] sm:min-h-[68px] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 lg:gap-6">
            <div className="flex items-center gap-3 min-w-0 lg:flex-1">
              {/* Hamburger (solo schermi piccoli) */}
              <button
                type="button"
                onClick={() => setNavOpen(true)}
                data-testid="mobile-nav-toggle"
                aria-label="Apri menu"
                className="lg:hidden w-10 h-10 rounded-xl flex items-center justify-center shrink-0 active:scale-95 transition-transform"
                style={{ background: DS.navy, color: '#D4AB6E' }}
              >
                <Menu className="w-5 h-5" strokeWidth={2} />
              </button>
              <div className="min-w-0 flex-1">
                {subtitle && (
                  <p className="text-[9px] uppercase tracking-[0.28em] mb-1 font-semibold truncate" style={{ color: DS.gold }}>
                    {subtitle}
                  </p>
                )}
                <h1 className="font-serif font-light tracking-tight text-slate-900 leading-none truncate"
                  style={{ fontSize: 'clamp(1.15rem, 3.5vw, 1.75rem)', letterSpacing: '-0.03em' }}>
                  {title}
                </h1>
                <div className="mt-2 hidden sm:block page-hero-rule" />
              </div>
              {/* Campanella a destra del titolo su mobile */}
              {!isDesktop && (
                <div className="shrink-0" style={{ background: DS.navy, borderRadius: DS.r2 }}>
                  <NotificationBell />
                </div>
              )}
            </div>
            {/* Azioni: riga scorrevole su mobile, wrap su desktop */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 lg:overflow-visible lg:flex-wrap lg:justify-end">
              <GlobalCommandPalette />
              {actions}
              {isDesktop && (
                <div style={{ background: DS.navy, borderRadius: DS.r2 }}>
                  <NotificationBell />
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Page content ── */}
        <div className={`animate-page-in ${noPad ? 'pb-20 lg:pb-0' : 'px-4 sm:px-8 py-5 sm:py-7 pb-24 lg:pb-7'}`}>
          {children}
        </div>
      </main>

      {/* Bottom nav rapida (solo schermi piccoli) */}
      <div className="lg:hidden"><MobileBottomNav /></div>
      <BoostJoinBanner />
    </div>
  );
}
